import { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

// ============================================
// Types
// ============================================
interface DailyOpen {
  date: string;
  opens: number;
}

interface TierData {
  active: number;
  warm: number;
  onboarding: number;
  reengagement: number;
  total: number;
}

interface EspTier extends TierData {
  esp_id: string;
  geo: string;
  provider: string;
}

interface CohortData {
  week: string;
  week_start: string;
  recruited: number;
  converted: number;
  conv_rate: number;
  avg_sends_to_convert: number;
}

interface TagConversion {
  arrival_tag: string;
  recruited: number;
  converted: number;
  conv_rate: number;
}

interface ConditionalRate {
  condition: string;
  total: number;
  converted: number;
  conv_rate: number;
}

interface TierHistoryPoint {
  date: string;
  active: number;
  warm: number;
  onboarding: number;
  reengagement: number;
  total: number;
  openers: number;
}

interface EspOption {
  geo: string;
  provider: string;
  esp_id: string;
}

interface AnalyticsData {
  overview: {
    total_active: number;
    total_openers: number;
    opener_rate: number;
    fresh_recruits_week: number;
    daily_opens_7d: DailyOpen[];
  };
  tiers: {
    aggregate: TierData;
    per_esp: EspTier[];
  };
  tier_history: TierHistoryPoint[];
  conversion_funnel: CohortData[];
  conversion_by_tag: TagConversion[];
  conditional_rates: ConditionalRate[];
  available_esps: EspOption[];
}

// ============================================
// Helper Components
// ============================================

function KpiCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className={`${color} rounded-xl p-5 shadow-sm border border-opacity-20`}>
      <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function TierDonut({ tiers }: { tiers: TierData }) {
  const total = tiers.total || 1;
  const segments = [
    { label: 'Active', count: tiers.active, color: '#22c55e', bg: 'bg-green-100 text-green-800' },
    { label: 'Warm', count: tiers.warm, color: '#eab308', bg: 'bg-yellow-100 text-yellow-800' },
    { label: 'Onboarding', count: tiers.onboarding, color: '#3b82f6', bg: 'bg-blue-100 text-blue-800' },
    { label: 'Re-engagement', count: tiers.reengagement, color: '#ef4444', bg: 'bg-red-100 text-red-800' },
  ];

  // Build conic-gradient
  let cumulative = 0;
  const gradientParts: string[] = [];
  segments.forEach(s => {
    const pct = (s.count / total) * 100;
    gradientParts.push(`${s.color} ${cumulative}% ${cumulative + pct}%`);
    cumulative += pct;
  });

  return (
    <div className="flex items-center gap-8">
      <div
        className="w-40 h-40 rounded-full flex-shrink-0"
        style={{
          background: `conic-gradient(${gradientParts.join(', ')})`,
        }}
      >
        <div className="w-full h-full rounded-full flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-inner">
            <span className="text-lg font-bold text-gray-700">{total.toLocaleString()}</span>
          </div>
        </div>
      </div>
      <div className="space-y-2 flex-1">
        {segments.map(s => (
          <div key={s.label} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-sm font-medium text-gray-700">{s.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.bg}`}>
                {((s.count / total) * 100).toFixed(1)}%
              </span>
              <span className="text-sm text-gray-500 w-16 text-right">{s.count.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniSparkline({ data }: { data: DailyOpen[] }) {
  if (!data || data.length === 0) return <span className="text-gray-400 text-sm">No data yet</span>;
  const max = Math.max(...data.map(d => d.opens), 1);
  const barWidth = 100 / data.length;

  return (
    <div className="flex items-end gap-1 h-12">
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center flex-1">
          <div
            className="w-full bg-indigo-400 rounded-t-sm min-h-[2px] transition-all"
            style={{ height: `${(d.opens / max) * 48}px` }}
            title={`${d.date}: ${d.opens} opens`}
          />
          <span className="text-[8px] text-gray-400 mt-0.5">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEsp, setSelectedEsp] = useState('all');
  const [tierHistoryEsp, setTierHistoryEsp] = useState('all');
  const [tierHistoryData, setTierHistoryData] = useState<TierHistoryPoint[] | null>(null);
  const [tierHistoryLoading, setTierHistoryLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedEsp !== 'all') {
        params.set('esp', selectedEsp);
      } else {
        params.set('geo', 'all');
      }
      const resp = await fetch(`${API_BASE_URL}/api/analytics?${params.toString()}`);
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      const json = await resp.json();
      setData(json);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedEsp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch tier history independently when the local ESP selector changes
  useEffect(() => {
    if (tierHistoryEsp === 'all') {
      // Use the data from the main fetch
      setTierHistoryData(null);
      return;
    }
    const fetchTierHistory = async () => {
      setTierHistoryLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/analytics/tier-history?esp=${tierHistoryEsp}`);
        if (!resp.ok) throw new Error(`API error: ${resp.status}`);
        const json = await resp.json();
        setTierHistoryData(json.tier_history || []);
      } catch (e) {
        console.error('Failed to fetch tier history:', e);
        setTierHistoryData([]);
      } finally {
        setTierHistoryLoading(false);
      }
    };
    fetchTierHistory();
  }, [tierHistoryEsp]);

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header + ESP Filter */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📊 Engagement Analytics</h1>
            <p className="text-gray-500 text-sm mt-1">Subscriber tiers, conversion tracking, and daily opens</p>
          </div>
          <select
            id="esp-filter"
            value={selectedEsp}
            onChange={e => setSelectedEsp(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All ESPs</option>
            {data?.available_esps?.map(e => (
              <option key={e.esp_id} value={e.esp_id}>
                {e.esp_id.toUpperCase()} ({e.provider} / {e.geo.toUpperCase()})
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <strong>Error:</strong> {error}
          </div>
        )}

        {data && !loading && (
          <div className="space-y-6">
            {/* === Section 1: KPI Bar === */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard
                label="Active Subscribers"
                value={data.overview.total_active}
                color="bg-white"
              />
              <KpiCard
                label="Opener Rate"
                value={`${data.overview.opener_rate}%`}
                sub={`${data.overview.total_openers.toLocaleString()} openers`}
                color="bg-white"
              />
              <KpiCard
                label="Fresh Recruits (7d)"
                value={data.overview.fresh_recruits_week}
                color="bg-white"
              />
              <div className="bg-white rounded-xl p-5 shadow-sm border">
                <p className="text-sm font-medium text-gray-600 mb-2">Daily Opens (7d)</p>
                <MiniSparkline data={data.overview.daily_opens_7d} />
              </div>
            </div>

            {/* === Section 2: Tier Distribution === */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">🏗️ Tier Distribution</h2>
                <TierDonut tiers={data.tiers.aggregate} />
              </div>

              {/* Per-ESP Tier Table */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Per ESP Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="text-left py-2 font-medium">ESP</th>
                        <th className="text-right py-2 font-medium">Active</th>
                        <th className="text-right py-2 font-medium">Warm</th>
                        <th className="text-right py-2 font-medium">Onboard</th>
                        <th className="text-right py-2 font-medium">Re-eng</th>
                        <th className="text-right py-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tiers.per_esp.map(e => (
                        <tr key={e.esp_id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 font-medium text-gray-800">{e.esp_id.toUpperCase()}</td>
                          <td className="py-2 text-right text-green-600">{e.active.toLocaleString()}</td>
                          <td className="py-2 text-right text-yellow-600">{e.warm.toLocaleString()}</td>
                          <td className="py-2 text-right text-blue-600">{e.onboarding.toLocaleString()}</td>
                          <td className="py-2 text-right text-red-600">{e.reengagement.toLocaleString()}</td>
                          <td className="py-2 text-right font-semibold text-gray-700">{e.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* === Section 2b: Actives Over Time === */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">📈 Actives Over Time</h2>
                <select
                  id="tier-history-esp-filter"
                  value={tierHistoryEsp}
                  onChange={e => setTierHistoryEsp(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All ESPs</option>
                  {data.available_esps?.map(e => (
                    <option key={e.esp_id} value={e.esp_id}>
                      {e.provider.charAt(0).toUpperCase() + e.provider.slice(1)} ({e.geo.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
              {tierHistoryLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin h-6 w-6 border-3 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              ) : (() => {
                const history = tierHistoryData ?? data.tier_history;
                return (!history || history.length === 0) ? (
                  <p className="text-gray-400 text-sm">No historical data yet. Snapshots are captured daily after each allocation run.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 border-b">
                          <th className="text-left py-2 font-medium">Date</th>
                          <th className="text-right py-2 font-medium">Active</th>
                          <th className="text-right py-2 font-medium">Warm</th>
                          <th className="text-right py-2 font-medium">Onboard</th>
                          <th className="text-right py-2 font-medium">Re-eng</th>
                          <th className="text-right py-2 font-medium">Total</th>
                          <th className="text-right py-2 font-medium">Δ Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h, i) => {
                          const prev = i > 0 ? history[i - 1] : null;
                          const delta = prev ? h.active - prev.active : 0;
                          return (
                            <tr key={h.date} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-2 text-gray-700 font-mono text-xs">{h.date}</td>
                              <td className="py-2 text-right font-semibold text-green-600">{h.active.toLocaleString()}</td>
                              <td className="py-2 text-right text-yellow-600">{h.warm.toLocaleString()}</td>
                              <td className="py-2 text-right text-blue-600">{h.onboarding.toLocaleString()}</td>
                              <td className="py-2 text-right text-red-600">{h.reengagement.toLocaleString()}</td>
                              <td className="py-2 text-right text-gray-700">{h.total.toLocaleString()}</td>
                              <td className={`py-2 text-right font-medium ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-400'
                                }`}>
                                {i > 0 ? (delta > 0 ? `+${delta}` : delta === 0 ? '—' : String(delta)) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* === Section 3: Conversion Funnel === */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🌱 Fresh Recruit Conversion (Weekly Cohorts)</h2>
              {data.conversion_funnel.length === 0 ? (
                <p className="text-gray-400 text-sm">No cohort data available yet. Data accumulates from March 6, 2026.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="text-left py-2 font-medium">Cohort</th>
                        <th className="text-right py-2 font-medium">Recruited</th>
                        <th className="text-right py-2 font-medium">Converted</th>
                        <th className="text-right py-2 font-medium">Conv %</th>
                        <th className="text-right py-2 font-medium">Avg Sends</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.conversion_funnel.map((c, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-2 text-gray-700">{c.week}</td>
                          <td className="py-2 text-right font-medium">{c.recruited.toLocaleString()}</td>
                          <td className="py-2 text-right text-green-600 font-medium">{c.converted.toLocaleString()}</td>
                          <td className="py-2 text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.conv_rate > 20 ? 'bg-green-100 text-green-800' :
                                c.conv_rate > 5 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-600'
                              }`}>
                              {c.conv_rate}%
                            </span>
                          </td>
                          <td className="py-2 text-right text-gray-500">{c.avg_sends_to_convert || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* === Section 4 + 5: Tag Conversion + Conditional Rates === */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversion by Tag */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">🏷️ Conversion by Arrival Tag</h2>
                {data.conversion_by_tag.length === 0 ? (
                  <p className="text-gray-400 text-sm">No tag data yet. Will populate as new recruits are allocated.</p>
                ) : (
                  <div className="space-y-3">
                    {data.conversion_by_tag.map(t => (
                      <div key={t.arrival_tag} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-mono">{t.arrival_tag}</span>
                          <span className="text-xs text-gray-400">{t.recruited.toLocaleString()} recruits</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${Math.min(t.conv_rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-700 w-12 text-right">{t.conv_rate}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Conditional Rates */}
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">📈 Conversion by Send Count</h2>
                {data.conditional_rates.every(r => r.total === 0) ? (
                  <p className="text-gray-400 text-sm">Needs 2-4 weeks of data. <code>send_count</code> was just deployed.</p>
                ) : (
                  <div className="space-y-3">
                    {data.conditional_rates.map(r => (
                      <div key={r.condition} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{r.condition}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">{r.total.toLocaleString()} subs</span>
                          <span className={`text-sm font-semibold w-12 text-right ${r.conv_rate > 20 ? 'text-green-600' :
                              r.conv_rate > 5 ? 'text-yellow-600' :
                                'text-gray-500'
                            }`}>
                            {r.conv_rate}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
